import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import {
  getUserSubscriptionTier,
  REPORT_LIMITS,
} from "@/utils/subscriptionUtils";

// PAYG report limit constant is defined in subscription utils (REPORT_LIMITS.PAYG)

export interface ReportMetadata {
  id?: string;
  user_id: string;
  continent: string;
  file_url: string;
  file_name: string;
  created_at?: string;
}

/**
 * Save report metadata to the database
 */
export const saveReport = async (
  userId: string,
  continent: string,
  fileUrl: string,
  fileName: string,
): Promise<boolean> => {
  try {
    const { error } = await supabase.from("reports").insert([
      {
        user_id: userId,
        continent,
        file_url: fileUrl,
        file_name: fileName,
      },
    ]);

    if (error) {
      console.error("Error saving report metadata:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Unexpected error saving report:", error);
    return false;
  }
};

/**
 * Check if a user has reached their report generation limit within the current month
 * Returns true if the user is allowed to generate more reports
 */
export const canGenerateReport = async (
  user: User | null,
): Promise<{
  allowed: boolean;
  remaining: number;
  message: string;
}> => {
  // If no user, deny access
  if (!user) {
    return {
      allowed: false,
      remaining: 0,
      message: "You must be logged in to generate reports.",
    };
  }

  try {
    // Get user's subscription tier
    const tier = await getUserSubscriptionTier(user.id);
    const reportLimit = REPORT_LIMITS[tier];

    // Professional users have unlimited reports
    if (reportLimit === null) {
      return {
        allowed: true,
        remaining: Infinity,
        message:
          "You can generate unlimited reports with your Professional plan.",
      };
    }

    // Calculate start of current month
    const now = new Date();
    const startOfMonth = new Date(
      now.getFullYear(),
      now.getMonth(),
      1,
    ).toISOString();

    // Count reports generated in the current month
    const { data, error, count } = await supabase
      .from("reports")
      .select("id", { count: "exact" })
      .eq("user_id", user.id)
      .gte("created_at", startOfMonth);

    if (error) {
      console.error("Error checking report count:", error);
      // On error, allow generation to prevent blocking users
      return {
        allowed: true,
        remaining: reportLimit,
        message: "Unable to check report limit. Please try again later.",
      };
    }

    const reportsGenerated = count || 0;
    const remaining = Math.max(0, reportLimit - reportsGenerated);

    const upgradeTier = tier === "PAYG" ? "Professional" : "Professional";

    return {
      allowed: reportsGenerated < reportLimit,
      remaining,
      message:
        remaining > 0
          ? `You have ${remaining} report generations remaining this month.`
          : `You have reached your monthly report limit. Upgrade to ${upgradeTier} for ${tier === "PAYG" ? "more" : "unlimited"} reports.`,
    };
  } catch (error) {
    console.error("Unexpected error checking report limit:", error);
    // On error, allow generation to prevent blocking users
    return {
      allowed: true,
      remaining: REPORT_LIMITS["PAYG"] ?? 0,
      message: "Unable to check report limit. Please try again later.",
    };
  }
};

/**
 * Get all reports for a user
 */
export const getUserReports = async (
  userId: string,
): Promise<ReportMetadata[]> => {
  try {
    const { data, error } = await supabase
      .from("reports")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching user reports:", error);
      return [];
    }

    return data as ReportMetadata[];
  } catch (error) {
    console.error("Unexpected error fetching reports:", error);
    return [];
  }
};

export const deleteReport = async (
  reportId: string,
  userId: string,
): Promise<void> => {
  // First get the report to get the file name
  const { data: report, error: fetchError } = await supabase
    .from("reports")
    .select("file_name")
    .eq("id", reportId)
    .eq("user_id", userId)
    .single();

  if (fetchError) throw fetchError;

  // Construct the storage path directly from userId + file_name
  // (avoids brittle URL parsing which breaks for private bucket URLs)
  const filePath = `${userId}/${report.file_name}`;

  // Delete the file from storage
  const { error: storageError } = await supabase.storage
    .from("reports")
    .remove([filePath]);

  if (storageError) throw storageError;

  // Delete the record from the database
  const { error: deleteError } = await supabase
    .from("reports")
    .delete()
    .eq("id", reportId)
    .eq("user_id", userId);

  if (deleteError) throw deleteError;
};
